import {
  MAX_PASSWORD_LENGTH,
  MIN_PASSWORD_LENGTH,
  PASSWORD_PATTERN,
} from "../constants/app";
import {
  PASSWORD_STRENGTH_COMMON_WORD_PENALTY,
  PASSWORD_STRENGTH_COMMON_WORDS,
  PASSWORD_STRENGTH_KEYBOARD_ROWS,
  PASSWORD_STRENGTH_LENGTH_THRESHOLDS,
  PASSWORD_STRENGTH_MIN_RUN_LENGTH,
  PASSWORD_STRENGTH_PREDICTABLE_RUN_PENALTY,
  PASSWORD_STRENGTH_SUBSTITUTIONS,
} from "../constants/password-strength";
import {
  EPasswordStrength,
  type IGetPasswordStrengthProps,
  type IPasswordStrength,
} from "./password-strength.types";

interface IPasswordTextProps {
  text: string;
}

const isPolicyMet = ({ text }: IPasswordTextProps): boolean =>
  text.length >= MIN_PASSWORD_LENGTH &&
  text.length <= MAX_PASSWORD_LENGTH &&
  PASSWORD_PATTERN.test(text);

const containsCommonWord = ({ text }: IPasswordTextProps): boolean => {
  const normalized = Array.from(text.toLowerCase())
    .map((character) => PASSWORD_STRENGTH_SUBSTITUTIONS[character] ?? character)
    .join("");

  return PASSWORD_STRENGTH_COMMON_WORDS.some((word) =>
    normalized.includes(word),
  );
};

const isPredictableWindow = ({ text }: IPasswordTextProps): boolean => {
  const codes = Array.from(text, (character) => character.codePointAt(0) ?? 0);
  const steps = codes.slice(1).map((code, index) => code - codes[index]);
  const firstStep = steps[0];

  // NOTE: one step repeated across the window: 0 is "aaaa", ±1 is "abcd" or
  // "4321".
  if (Math.abs(firstStep) <= 1 && steps.every((step) => step === firstStep)) {
    return true;
  }

  const reversed = Array.from(text).reverse().join("");

  return PASSWORD_STRENGTH_KEYBOARD_ROWS.some(
    (row) => row.includes(text) || row.includes(reversed),
  );
};

const containsPredictableRun = ({ text }: IPasswordTextProps): boolean => {
  const characters = Array.from(text.toLowerCase());

  for (
    let start = 0;
    start + PASSWORD_STRENGTH_MIN_RUN_LENGTH <= characters.length;
    start++
  ) {
    const segment = characters
      .slice(start, start + PASSWORD_STRENGTH_MIN_RUN_LENGTH)
      .join("");

    if (isPredictableWindow({ text: segment })) return true;
  }

  return false;
};

// NOTE: NSAI-5324 — an in-house heuristic rather than zxcvbn: no dependency and
// no bundle cost on web, mobile or the extension, and every rule is a named
// constant a test can pin. A password that fails the policy is always weak; a
// policy-compliant one starts at medium, gains a point per length threshold and
// loses points for a common base word or a predictable run.
export const getPasswordStrength = ({
  password,
}: IGetPasswordStrengthProps): IPasswordStrength => {
  if (!isPolicyMet({ text: password })) {
    return { level: EPasswordStrength.WEAK, isPolicyMet: false };
  }

  const lengthPoints = PASSWORD_STRENGTH_LENGTH_THRESHOLDS.filter(
    (threshold) => password.length >= threshold,
  ).length;
  const commonWordPenalty = containsCommonWord({ text: password })
    ? PASSWORD_STRENGTH_COMMON_WORD_PENALTY
    : 0;
  const predictableRunPenalty = containsPredictableRun({ text: password })
    ? PASSWORD_STRENGTH_PREDICTABLE_RUN_PENALTY
    : 0;
  const points = lengthPoints - commonWordPenalty - predictableRunPenalty;

  if (points < 0) return { level: EPasswordStrength.WEAK, isPolicyMet: true };
  if (points === 0) {
    return { level: EPasswordStrength.MEDIUM, isPolicyMet: true };
  }

  return { level: EPasswordStrength.STRONG, isPolicyMet: true };
};
